import Timeline from '@/components/ui/timeline/Timeline';
import { DATE_FORMATS } from '@/constants/app/date';
import type { TransactionDetail } from '@/types';
import { useModal } from '@bitstopco/bitstop-theme';
import { formatDate } from '@bitstopco/bitstop-theme/helpers';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';

type TransactionStatusLogTabProps = {
  transaction: TransactionDetail;
};

const NEUTRAL_COLOR = '#9CA3AF';

const TransactionStatusLogEmpty = () => {
  return (
    <Stack py={6} gap={0} textAlign="center">
      <Typography variant="h5" color="text.secondary">
        No data available
      </Typography>
    </Stack>
  );
};

const TransactionStatusLog = ({ transaction }: TransactionStatusLogTabProps) => {
  const { show } = useModal();

  const handleUnfreezeTransaction = () => {
    show('unfreeze-transaction', { id: transaction.id, reason: getReason() });
  };

  const handleMarkAsRefund = () => {
    show('refund-transaction', { id: transaction.id, reason: getReason() });
  };

  // Color for both the timeline dot and the "to" side of a stage's transition pill.
  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'partial':
      case 'batched':
      case 'sending':
      case 'unfrozen':
        return 'info.main';
      case 'completed':
      case 'refunded':
        return 'success.main';
      case 'frozen':
        return '#666';
      case 'under_review':
        return 'warning.main';
      case 'dispense_failure':
        return 'error.main';
      case 'collecting_cash':
      case 'dispensing':
        return NEUTRAL_COLOR;
      default:
        return NEUTRAL_COLOR;
    }
  };

  const getReason = () => {
    let frozen_reason: string | null = null;
    const sale_audit = transaction.sale_audits.find((audit) => audit.stage === 'frozen');
    if (!sale_audit) return null;
    if (sale_audit.data?.triggers && sale_audit.data.triggers.length > 0) {
      frozen_reason = sale_audit.data?.triggers.map((t) => t.rule_name).join(', ');
    }
    return frozen_reason;
  };

  // Headline shown above the pill. Routine stages get none, same as the reference design --
  // only the stage worth calling out on its own line gets one.
  const getHeadline = (stage: string) => {
    switch (stage) {
      case 'partial':
        return 'Transaction created';
      case 'batched':
        return 'Transaction batched';
      case 'sending':
        return 'Transaction sending';
      case 'unfrozen':
        return 'Transaction released';
      case 'under_review':
        return 'Transaction under review';
      case 'refunded':
        return 'Transaction refunded';
      case 'completed':
        return 'Transaction completed';
      case 'frozen':
        return 'Transaction frozen';
      case 'dispense_failure':
        return 'Dispense failure detected';
      default:
        return '';
    }
  };

  const formatStageLabel = (stage: string) =>
    stage.replaceAll('_', ' ').replace(/\b\w/g, (char) => char.toUpperCase());

  const items = useMemo(() => {
    const sorted_audits = transaction.sale_audits.toSorted(
      (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
    );

    return sorted_audits.map((sale_audit, index) => {
      const next_audit = index < sorted_audits.length ? sorted_audits[index + 1] : null;
      const frozen_reason = getReason();
      const headline = getHeadline(sale_audit.stage);
      const stageColor = getStageColor(sale_audit.stage);

      return {
        icon: (
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: stageColor,
            }}
          />
        ),
        name: 'test',
        content: (
          <Stack direction="column" alignItems="flex-start" sx={{ pb: 3 }}>
            {headline && (
              <Typography variant="body2" fontWeight="bold" sx={{ color: stageColor, mb: 0.5 }}>
                {headline}
              </Typography>
            )}
            {sale_audit.stage === 'frozen' && frozen_reason && (
              <Typography variant="caption" color="text.secondary">
                Reason: {frozen_reason}
              </Typography>
            )}
            {sale_audit.stage === 'unfrozen' && sale_audit.released_by && (
              <Typography variant="caption" color="text.secondary">
                Released by: {sale_audit.released_by}
              </Typography>
            )}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.75,
                mt: 0.5,
                borderRadius: '8px',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }}
            >
              {next_audit && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {formatStageLabel(next_audit.stage)}
                  </Typography>
                  <ArrowForwardIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                </>
              )}
              <Typography variant="body2" fontWeight={600} sx={{ color: stageColor }}>
                {formatStageLabel(sale_audit.stage)}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75 }}>
              {formatDate(sale_audit.updated_at, DATE_FORMATS.dateHoursLocal)}
            </Typography>
            {index === 0 && sale_audit.stage === 'under_review' && (
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleUnfreezeTransaction}
                >
                  Unfreeze transaction
                </Button>
                <Button variant="outlined" size="small" onClick={handleMarkAsRefund}>
                  Mark as a refund <RefreshIcon sx={{ fontSize: 16, ml: '7px' }} />
                </Button>
              </Stack>
            )}
          </Stack>
        ),
      };
    });
  }, [transaction]);

  return (
    <Box pt={3}>
      <Timeline
        items={items}
        dotProps={{
          sx: {
            p: 0,
            border: '0px',
            boxShadow: 'none',
            background: 'transparent',
          },
        }}
      />
    </Box>
  );
};

const TransactionStatusLogTab = ({ transaction }: TransactionStatusLogTabProps) =>
  transaction.sale_audits.length > 0 ? (
    <TransactionStatusLog transaction={transaction} />
  ) : (
    <TransactionStatusLogEmpty />
  );

export default TransactionStatusLogTab;
